import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { StatusBadge } from "./StatusBadge";
import { CaseFilePlaceholder } from "./CaseFilePlaceholder";
import { PLATFORM_COLORS } from "../lib/constants";
import { ChevronUp } from "lucide-react";

export const HeroBanner = () => {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports", { params: { status: "all", sort: "recent" } })
      .then(({ data }) => {
        // Only CONFIRMED cheaters go in the banner — never unverified suspects.
        const confirmed = data.filter((r) => r.status === "confirmed_cheater" || r.status === "banned");
        // Prefer ones with a real screenshot, but keep branded placeholders too.
        confirmed.sort((a, b) => (b.images?.length ? 1 : 0) - (a.images?.length ? 1 : 0));
        setItems(confirmed.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % items.length), 4500);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <div data-testid="hero-banner" className="relative h-56 w-full overflow-hidden border-b border-[#1E293F] sm:h-72">
      {items.map((r, idx) => {
        const cover = r.images && r.images[0];
        const accent = PLATFORM_COLORS[r.platform] || "#00F0FF";
        return (
          <button
            key={r.id}
            onClick={() => navigate(`/players/${r.platform}/${encodeURIComponent(r.gamertag)}`)}
            className="absolute inset-0 w-full text-left transition-opacity duration-1000 ease-in-out"
            style={{ opacity: idx === i ? 1 : 0, pointerEvents: idx === i ? "auto" : "none" }}
          >
            {cover
              ? <img src={fileUrl(cover)} alt={r.gamertag} className="h-full w-full scale-105 object-cover" style={{ animation: idx === i ? "kenburns 6s ease-out both" : "none" }} />
              : <CaseFilePlaceholder report={r} />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#090C15] via-[#090C15]/60 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 mx-auto flex max-w-7xl flex-col gap-2 px-4 py-5 sm:px-6">
              <div className="flex items-center gap-2">
                <StatusBadge status={r.status} />
                <span className="rounded px-1.5 py-0.5 font-mono text-xs font-semibold" style={{ color: accent, backgroundColor: `${accent}22` }}>{r.platform}</span>
              </div>
              <h2 className="font-heading text-2xl font-black tracking-tight text-white sm:text-4xl">{r.gamertag}</h2>
              <div className="flex items-center gap-2 text-sm text-[#CBD5E1]">
                <span className="rounded border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-2 py-0.5 font-semibold text-[#FF2A55]">{r.cheat_type}</span>
                <span className="flex items-center gap-1 text-[#94A3B8]"><ChevronUp size={13} className="text-[#FF2A55]" /> {r.upvotes} faced</span>
              </div>
            </div>
          </button>
        );
      })}
      <div className="absolute bottom-3 right-4 z-10 flex gap-1.5">
        {items.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} data-testid={`hero-dot-${idx}`}
            className="h-1.5 rounded-full transition-all" style={{ width: idx === i ? 20 : 8, backgroundColor: idx === i ? "#00F0FF" : "#475569" }} />
        ))}
      </div>
    </div>
  );
};
